import React from 'react'
import { RealEstateCategory, SegmentationType, TransactionType } from '../../../../../../@types/types'
import { Flow, FlowName, FlowScreenElement } from '../../../../../../@types/flow-types'
import { PROPERTY_OWNER_HOUSE_RENT_FLOW_PATH } from '../../../../../../services/routes/routes'
import { propertyOwnerHouseRentSchema } from './propertyOwnerHouseRentSchema'
import {
  SCREEN_ADDRESS,
  SCREEN_BASIC_INFO,
  SCREEN_CONSTRUCTION,
  SCREEN_CONTACT_INFO,
  SCREEN_COSTS,
  SCREEN_DESCRIPTION,
  SCREEN_MULTIMEDIA,
  SCREEN_OTHER_FACILITIES,
  SCREEN_TENANT_DOCUMENTS,
  SCREENS,
} from '../../../../../../services/domain/wizardScreens'
import ContactInfo from '../../../../../../components/pages/insertionPage/screens/contactInfo/ContactInfo'
import Address from '../../../../../../components/pages/insertionPage/screens/address/Address'
import { priceToApiMonthlyRent } from '../../../../../../api/realEstate/converters/toApi/estate/price/priceToApi'
import Construction from '../../../../../../components/pages/insertionPage/screens/construction/Construction'
import Multimedia from '../../../../../../components/pages/insertionPage/screens/multimediaNew/Multimedia'

import { MultimediaUploaderType } from '../../../../../../@types/multimedia-types'
import ResidentialCostsRent from '../../../../../../components/pages/insertionPage/screens/costs/wrappers/residential/ResidentialCostsRent'
import Description from '../../../../../../components/pages/insertionPage/screens/description/Description'
import OtherFacilities from '../../../../../../components/pages/insertionPage/screens/otherFacilities/OtherFacilities'
import {
  bathroomField,
  interiorQuality,
  noBedrooms,
  parkingSpace,
  petsAllowed,
  totalFloorsField,
  usableAreaField,
} from '../../../../../../components/pages/insertionPage/screens/otherFacilities/wrapper/otherFacilitiesFieldsWrapper'
import { BuildingTypeOption } from '../../../../../../@types/dropdown-types'
import { getDefaultClasses } from '../../../../../../utils/style-utils'
import { buildingTypeField } from '../../../../../../components/pages/insertionPage/screens/commonFields/buildingType/BuildingType'
import BasicInfo from '../../../../../../components/pages/insertionPage/screens/basicInfo/BasicInfo'
import {
  livingSpaceField,
  plotAreaField,
  priceField,
  priceMaximizer,
  roomField,
} from '../../../../../../components/pages/insertionPage/screens/basicInfo/wrappers/basicInfoFieldsWrapper'
import { PriceFieldType } from '../../../../../../components/pages/insertionPage/screens/basicInfo/fields/priceField/PriceField'
import TenantDocumentsScreen from '../../../../../../components/pages/insertionPage/screens/tenantDocuments/TenantDocumentsScreen'
import { houseRentEstateFeatures } from '../common'

const propertyOwnerHouseRentFlowScreens: FlowScreenElement[] = [
  {
    screen: SCREENS[SCREEN_ADDRESS],
    element: (props) => <Address {...props} isPriceOverviewLinkDisplayed={true} />,
  },
  {
    screen: SCREENS[SCREEN_BASIC_INFO],
    element: (props) => (
      <BasicInfo
        {...props}
        screenTooltipKey='tooltip.screens.basicInfo.apartmentHouseOrSTARent'
        properties={[
          priceField(PriceFieldType.COLD_RENT, getDefaultClasses('left')),
          roomField(getDefaultClasses('right')),
          livingSpaceField(),
          plotAreaField('right', undefined),
          priceMaximizer(),
        ]}
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_COSTS],
    element: (props) => (
      <ResidentialCostsRent {...props} showMoveInDate={true} screenTooltipKey='tooltip.screens.costs.allRent' />
    ),
  },
  {
    screen: SCREENS[SCREEN_OTHER_FACILITIES],
    element: (props) => (
      <OtherFacilities
        {...props}
        screenTooltipKey='tooltip.screens.otherFacilities.allRent'
        properties={[
          houseRentEstateFeatures,
          interiorQuality(),
          petsAllowed(),
          buildingTypeField('house', [
            BuildingTypeOption.SINGLE_FAMILY_HOUSE,
            BuildingTypeOption.MID_TERRACE_HOUSE,
            BuildingTypeOption.END_TERRACE_HOUSE,
            BuildingTypeOption.MULTI_FAMILY_HOUSE,
            BuildingTypeOption.BUNGALOW,
            BuildingTypeOption.FARMHOUSE,
            BuildingTypeOption.SEMIDETACHED_HOUSE,
            BuildingTypeOption.VILLA,
            BuildingTypeOption.CASTLE_MANOR_HOUSE,
            BuildingTypeOption.SPECIAL_REAL_ESTATE,
            BuildingTypeOption.OTHER,
          ]),
          totalFloorsField(getDefaultClasses('right')),
          bathroomField(getDefaultClasses('left')),
          noBedrooms('right'),
          usableAreaField(),
          parkingSpace({ showParkingSpaceType: true, hasGarageLabel: true }),
        ]}
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_DESCRIPTION],
    element: (props) => <Description {...props} hasFurnishingNote={true} hasLocationNote={true} hasOtherNote={true} />,
  },
  {
    screen: SCREENS[SCREEN_MULTIMEDIA],
    element: (props) => (
      <Multimedia
        {...props}
        doCloseMultimediaStatusMessage={props.doCloseMultimediaStatusMessage!}
        allowedTypesToDisplay={[
          MultimediaUploaderType.PICTURE,
          MultimediaUploaderType.FLOOR_PLAN,
          MultimediaUploaderType.DOCUMENT,
          MultimediaUploaderType.VIDEO,
        ]}
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_CONSTRUCTION],
    element: (props) => <Construction {...props} hasLastModernizationField={true} />,
  },
  {
    screen: SCREENS[SCREEN_TENANT_DOCUMENTS],
    element: (props) => <TenantDocumentsScreen {...props} />,
  },
  {
    screen: SCREENS[SCREEN_CONTACT_INFO],
    element: (props) => <ContactInfo {...props} />,
  },
]

export const propertyOwnerHouseRentFlow: Flow = {
  flowInformation: {
    owner: 'wizard.navigation.header.owner',
    estateType: 'wizard.navigation.header.house',
    offerType: 'wizard.navigation.header.rents',
    path: PROPERTY_OWNER_HOUSE_RENT_FLOW_PATH,
    name: FlowName.PROPERTY_OWNER_HOUSE_RENT,
    validationSchema: propertyOwnerHouseRentSchema,
    options: {
      hasTextSuggestions: true,
      depositCalculationEnabled: true,
    },
    metaData: {
      category: RealEstateCategory.HOUSE,
      segmentation: SegmentationType.PROPERTY_OWNER,
      transaction: TransactionType.RENT,
    },
    priceToApiMapper: priceToApiMonthlyRent,
  },
  defaultStateValues: {
    heatingCostsOption: true,
    unknownConstructionYear: false,
    hideStreetAndStreetNumber: false,
  },
  flowScreens: propertyOwnerHouseRentFlowScreens,
}
